import * as Sentry from '@sentry/react';
import type { DeviceInfo, AudioSource } from '../stores';

const DIAG = '[AUDIO-BUG-12009:DeviceConfig]';

/**
 * DeviceConfigManager - Handles device enumeration, selection, mute/volume controls.
 *
 * Responsibilities:
 * - Enumerate available video/audio devices
 * - Manage device selection state
 * - Mute/unmute controls
 * - Volume management
 */
export type EnumeratedDeviceCounts = {
  audioinput: number;
  videoinput: number;
  audiooutput: number;
};

export class DeviceConfigManager {
  private cameras: DeviceInfo[] = [];
  private shadowcams: DeviceInfo[] = [];
  private microphones: AudioSource[] = [];
  private desktopAudioSources: AudioSource[] = [];
  private selectedCameraId: string | null = null;
  private selectedMicrophoneId: string | null = null;
  private selectedDesktopAudioSource: string | null = null;
  private videoMuted = false;
  private audioMuted = false;
  private volume = 1.0;
  private lastDeviceCounts: EnumeratedDeviceCounts = { audioinput: 0, videoinput: 0, audiooutput: 0 };
  private onDeviceListChanged: (() => void) | null = null;
  private deviceChangeHandler: (() => void) | null = null;

  public setOnDeviceListChanged(callback: () => void): void {
    this.onDeviceListChanged = callback;
  }

  /**
   * Subscribe to Chromium's `devicechange` event and re-enumerate when it fires.
   *
   * Critical for BLUE-STREAM-12010: PlayJector only auto-activates ShadowCam00
   * at launch — ShadowCam03 (desktop shadowcam) is activated reactively by
   * host-electron's `getSources` reply handler ~700 ms *after* the host-frontend
   * has already booted and called `enumerateDevices()` once. Without this
   * listener, the initial enumeration captures only ShadowCam00, the launch-time
   * health check fails, and `matchmakeableBlockReason='shadowcam_unhealthy'` is
   * latched for the entire session.
   *
   * Idempotent: safe to call repeatedly; only the first call subscribes.
   */
  public startDeviceChangeListener(): void {
    if (this.deviceChangeHandler) return;
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.addEventListener) return;
    this.deviceChangeHandler = () => {
      console.log(`${DIAG} devicechange event — re-enumerating`);
      void this.enumerateDevices();
    };
    navigator.mediaDevices.addEventListener('devicechange', this.deviceChangeHandler);
  }

  private stopDeviceChangeListener(): void {
    if (!this.deviceChangeHandler) return;
    if (typeof navigator !== 'undefined' && navigator.mediaDevices?.removeEventListener) {
      navigator.mediaDevices.removeEventListener('devicechange', this.deviceChangeHandler);
    }
    this.deviceChangeHandler = null;
  }

  public async enumerateDevices(): Promise<void> {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();

      const audioInputCount = devices.filter((d) => d.kind === 'audioinput').length;
      const videoInputCount = devices.filter((d) => d.kind === 'videoinput').length;
      const audioOutputCount = devices.filter((d) => d.kind === 'audiooutput').length;
      const emptyLabelCount = devices.filter((d) => !d.label).length;
      this.lastDeviceCounts = {
        audioinput: audioInputCount,
        videoinput: videoInputCount,
        audiooutput: audioOutputCount,
      };
      console.log(`${DIAG} Enumerated devices: ${audioInputCount} audioinput, ${videoInputCount} videoinput, ${audioOutputCount} audiooutput, ${emptyLabelCount} empty labels`);

      if (emptyLabelCount > 0) {
        console.warn(`${DIAG} ${emptyLabelCount} device(s) have empty labels — possible permissions issue`);
      }

      this.cameras = this.parseVideoDevices(devices);
      this.shadowcams = this.parseShadowcams(devices);
      this.microphones = this.parseMicrophones(devices);
      this.buildDesktopAudioSources(devices);
      this.onDeviceListChanged?.();
    } catch (error) {
      console.error(`${DIAG} Failed to enumerate devices:`, error);
      Sentry.captureException(error, {
        tags: { audio_stage: 'enumerate_devices', audio_diag: 'AUDIO-BUG-12009' },
      });
    }
  }

  public getCameras(): DeviceInfo[] {
    return this.cameras;
  }

  public getShadowcams(): DeviceInfo[] {
    return this.shadowcams;
  }

  public getEnumeratedDeviceCounts(): EnumeratedDeviceCounts {
    return { ...this.lastDeviceCounts };
  }

  public getMicrophones(): AudioSource[] {
    return this.microphones;
  }

  public getDesktopAudioSources(): AudioSource[] {
    return this.desktopAudioSources;
  }

  public selectCamera(deviceId: string): void {
    this.selectedCameraId = deviceId;
    localStorage.setItem('selectedCameraId', deviceId);
  }

  public selectMicrophone(deviceId: string): void {
    this.selectedMicrophoneId = deviceId;
    localStorage.setItem('selectedMicId', deviceId);
    this.microphones = this.microphones.map((mic) => ({
      ...mic,
      selected: mic.deviceId === deviceId,
    }));
  }

  public selectDesktopAudioSource(source: string): void {
    this.selectedDesktopAudioSource = source;
    localStorage.setItem('selectedDesktopAudioSource', source);
    this.desktopAudioSources = this.desktopAudioSources.map((s) => ({
      ...s,
      selected: s.deviceId === source,
    }));
  }

  public getSelectedCameraId(): string | null {
    return this.selectedCameraId;
  }

  public getSelectedMicrophoneId(): string | null {
    return this.selectedMicrophoneId;
  }

  public getSelectedDesktopAudioSource(): string | null {
    return this.selectedDesktopAudioSource;
  }

  public setVideoMuted(muted: boolean): void {
    this.videoMuted = muted;
  }

  public setAudioMuted(muted: boolean): void {
    this.audioMuted = muted;
  }

  public isVideoMuted(): boolean {
    return this.videoMuted;
  }

  public isAudioMuted(): boolean {
    return this.audioMuted;
  }

  public setVolume(volume: number): void {
    this.volume = Math.max(0, Math.min(1, volume));
  }

  public getVolume(): number {
    return this.volume;
  }

  public destroy(): void {
    this.stopDeviceChangeListener();
    this.onDeviceListChanged = null;
  }

  private parseVideoDevices(devices: MediaDeviceInfo[]): DeviceInfo[] {
    const cameras = devices.filter((d) => d.kind === 'videoinput' && !d.label.includes('ShadowCam'));
    const regex = / \([0-9a-fA-F]+:[0-9a-fA-F]+\)$/;
    return cameras.map((camera) => ({
      deviceId: camera.deviceId,
      displayName: camera.label.replace(regex, '').trim(),
    }));
  }

  private parseShadowcams(devices: MediaDeviceInfo[]): DeviceInfo[] {
    return devices
      .filter((d) => d.kind === 'videoinput' && d.label.includes('ShadowCam'))
      .map((d) => ({ deviceId: d.deviceId, displayName: d.label }));
  }

  private parseMicrophones(devices: MediaDeviceInfo[]): AudioSource[] {
    const mics = devices.filter(
      (d) => d.kind === 'audioinput' && !d.label.startsWith('Default') && !d.label.startsWith('Communications'),
    );
    const regex = / \([0-9a-fA-F]+:[0-9a-fA-F]+\)$/;
    const parsed = mics.map((mic) => ({
      deviceId: mic.deviceId,
      label: mic.label.replace(regex, '').trim(),
    }));
    const deduped = parsed.filter((mic, idx, self) => self.findIndex((m) => m.label === mic.label) === idx);
    const storedMicId = localStorage.getItem('selectedMicId');

    const sources: AudioSource[] = [
      { deviceId: '0', label: 'None', selected: !storedMicId || deduped.every((m) => m.deviceId !== storedMicId) },
      ...deduped.map((mic) => ({
        deviceId: mic.deviceId,
        label: mic.label,
        selected: storedMicId === mic.deviceId,
      })),
    ];

    return sources;
  }

  private buildDesktopAudioSources(devices: MediaDeviceInfo[]): void {
    const stored = localStorage.getItem('selectedDesktopAudioSource');
    this.desktopAudioSources = [
      { deviceId: '0', label: 'None', selected: stored === '0' },
      { deviceId: 'desktop', label: 'Desktop Audio', selected: stored === 'desktop' || !stored },
    ];

    const gameCaptureDevice = devices.find((d) => d.label.includes('AudioCaster Virtual Device'));
    if (gameCaptureDevice) {
      console.log(`${DIAG} AudioCaster present in device list: "${gameCaptureDevice.label}"`);
      this.desktopAudioSources.push({
        deviceId: 'gameCapture',
        label: 'Game Capture',
        selected: stored === 'gameCapture',
      });
    } else {
      console.warn(`${DIAG} AudioCaster NOT present in desktop audio source list`);
    }
  }
}
